import ReactGA from "react-ga4";

/**
 * Analytics Service - Replacement for AWS Pinpoint
 * Uses Google Analytics 4 for event tracking and user analytics
 */

export interface AnalyticsEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, string | number | boolean>;
}

export interface UserProperties {
  user_id?: string;
  email?: string;
  subscription_tier?: "free" | "premium";
  email_notifications?: boolean;
  marketing_emails?: boolean;
}

class AnalyticsService {
  private isInitialized = false;

  constructor() {
    this.checkInitialization();
  }

  private checkInitialization() {
    // Check if GA4 is initialized
    this.isInitialized = typeof window !== "undefined" && "gtag" in window;
  }

  /**
   * Track custom events (replacement for Pinpoint record())
   */
  trackEvent(event: AnalyticsEvent) {
    if (!this.isInitialized) {
      console.warn("Analytics not initialized");
      return;
    }

    try {
      ReactGA.event({
        action: event.action,
        category: event.category || "user_interaction",
        label: event.label,
        value: event.value,
        ...event.custom_parameters,
      });
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }

  /**
   * Set user properties (replacement for Pinpoint identifyUser())
   */
  setUserProperties(properties: UserProperties) {
    if (!this.isInitialized) {
      console.warn("Analytics not initialized");
      return;
    }

    try {
      // Set user ID if provided
      if (properties.user_id) {
        ReactGA.set({ user_id: properties.user_id });
      }

      // Set custom user properties
      const customProperties: Record<string, string | boolean> = {};

      if (properties.subscription_tier) {
        customProperties.subscription_tier = properties.subscription_tier;
      }

      if (properties.email_notifications !== undefined) {
        customProperties.email_notifications = properties.email_notifications;
      }

      if (properties.marketing_emails !== undefined) {
        customProperties.marketing_emails = properties.marketing_emails;
      }

      if (Object.keys(customProperties).length > 0) {
        ReactGA.gtag("config", "G-3ZC5BVTKEQ", {
          custom_map: customProperties,
        });
      }
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  /**
   * Track notification preferences update
   */
  trackNotificationPreferencesUpdate(preferences: {
    emailNotifications: boolean;
    marketingEmails: boolean;
    productUpdates: boolean;
    securityAlerts: boolean;
    skillShares: boolean;
    weeklyDigest: boolean;
  }) {
    this.trackEvent({
      action: "notification_preferences_updated",
      category: "user_settings",
      custom_parameters: {
        email_notifications: preferences.emailNotifications,
        marketing_emails: preferences.marketingEmails,
        product_updates: preferences.productUpdates,
        security_alerts: preferences.securityAlerts,
        skill_shares: preferences.skillShares,
        weekly_digest: preferences.weeklyDigest,
      },
    });
  }

  /**
   * Track cookie consent update
   */
  trackCookieConsentUpdate(consent: {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    source: string;
  }) {
    this.trackEvent({
      action: "cookie_consent_updated",
      category: "privacy",
      label: consent.source,
      custom_parameters: {
        necessary: consent.necessary,
        analytics: consent.analytics,
        marketing: consent.marketing,
      },
    });

    // Update GA4 consent settings
    if (typeof window !== "undefined" && "gtag" in window) {
      const gtag = (window as { gtag: (...args: unknown[]) => void }).gtag;
      gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
      });
    }
  }

  /**
   * Track notification sent events
   */
  trackNotificationSent(type: string, data: Record<string, unknown>) {
    this.trackEvent({
      action: "notification_sent",
      category: "notifications",
      label: type,
      custom_parameters: {
        notification_type: type,
        timestamp: new Date().toISOString(),
        ...data,
      },
    });
  }

  /**
   * Track skill sharing events
   */
  trackSkillShare(data: {
    recipientEmail: string;
    skillCount: number;
    shareType: string;
  }) {
    this.trackEvent({
      action: "skill_shared",
      category: "sharing",
      label: data.shareType,
      value: data.skillCount,
      custom_parameters: {
        skill_count: data.skillCount,
        share_type: data.shareType,
      },
    });
  }

  /**
   * Track user authentication events
   */
  trackAuthEvent(event: "sign_in" | "sign_out" | "sign_up") {
    this.trackEvent({
      action: event,
      category: "authentication",
    });
  }

  /**
   * Track page views (handled automatically by AnalyticsTracker component)
   */
  trackPageView(path: string, title?: string) {
    if (!this.isInitialized) {
      console.warn("Analytics not initialized");
      return;
    }

    try {
      ReactGA.send({
        hitType: "pageview",
        page: path,
        title: title,
      });
    } catch (error) {
      console.error("Failed to track page view:", error);
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export convenience functions for backward compatibility
export const trackEvent = (event: AnalyticsEvent) =>
  analyticsService.trackEvent(event);
export const setUserProperties = (properties: UserProperties) =>
  analyticsService.setUserProperties(properties);
export const trackNotificationPreferencesUpdate = (preferences: {
  emailNotifications: boolean;
  marketingEmails: boolean;
  productUpdates: boolean;
  securityAlerts: boolean;
  skillShares: boolean;
  weeklyDigest: boolean;
}) => analyticsService.trackNotificationPreferencesUpdate(preferences);
export const trackCookieConsentUpdate = (consent: {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  source: string;
}) => analyticsService.trackCookieConsentUpdate(consent);
export const trackNotificationSent = (
  type: string,
  data: Record<string, unknown>,
) => analyticsService.trackNotificationSent(type, data);
export const trackSkillShare = (data: {
  recipientEmail: string;
  skillCount: number;
  shareType: string;
}) => analyticsService.trackSkillShare(data);
export const trackAuthEvent = (event: "sign_in" | "sign_out" | "sign_up") =>
  analyticsService.trackAuthEvent(event);
